import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useGame } from '../GameContext';
import { Player, SoldierType, Army, SiegeEquipment } from '../types';
import ConfirmModal from './ConfirmModal';
import { gameService } from '../services/gameService';
import './MyArmy.css';

const MyArmy: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { gameState, resetPlayerArmy, linkUserIdToPlayer } = useGame();
  const [myPlayer, setMyPlayer] = useState<Player | null>(null);
  const [isLinking, setIsLinking] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [showResetModal, setShowResetModal] = useState<boolean>(false);

  // Local army state for batching changes
  const [localArmyCounts, setLocalArmyCounts] = useState<Record<SoldierType, number>>({
    'King': 0,
    'Knight': 0,
    'Archer': 0,
    'Crossbow': 0,
    'Healer': 0,
    'Siege Equipment': 0,
  });
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    const linkPlayer = async () => {
      if (isAuthenticated && user && user.sub) {
        // Helper function to get the actual Twitch username (same logic as App.tsx)
        const getTwitchUsername = (): string => {
          if (!user) return '';

          // Priority: custom claims from Actions > built-in fields
          return (user as any)['https://raid.nlith.it/user_name']     // From our Action
            || (user as any)['https://raid.nlith.it/username']        // Alternative from Action
            || (user as any)['https://twitch.tv/username']            // From old Action
            || (user as any).user_name                                // Built-in (usually not present)
            || user.nickname                                          // Fallback
            || user.name                                              // Last resort
            || '';
        };

        const twitchUsername = getTwitchUsername();

        // First, try to find player by userId
        let player = gameState.players.find(p => p.userId === user.sub);

        // If not found by userId, try to find by username (Twitch username)
        if (!player && twitchUsername && !isLinking) {
          const playerByName = gameState.players.find(
            p => (!p.userId || p.userId === '') && p.name.toLowerCase() === twitchUsername.toLowerCase()
          );

          // If found by name but not linked, link them
          if (playerByName) {
            setIsLinking(true);
            try {
              await linkUserIdToPlayer(playerByName.id, user.sub);
              // After linking, the gameState will update and trigger this effect again
              return;
            } catch (err) {
              console.error('Failed to link player:', err);
              setIsLinking(false);
            }
          }
        }

        setMyPlayer(player || null);
      }
    };

    linkPlayer();
  }, [isAuthenticated, user, gameState.players, linkUserIdToPlayer, isLinking]);

  // Sync local army counts with player's actual army
  useEffect(() => {
    if (myPlayer) {
      setLocalArmyCounts({
        'King': myPlayer.army.king ? 1 : 0,
        'Knight': myPlayer.army.knights.length,
        'Archer': myPlayer.army.archers.length,
        'Crossbow': myPlayer.army.crossbows.length,
        'Healer': myPlayer.army.healers.length,
        'Siege Equipment': myPlayer.army.siegeEquipment.length,
      });
      setHasPendingChanges(false);
    }
  }, [myPlayer]);

  const getUnitCount = (soldierType: SoldierType): number => {
    return localArmyCounts[soldierType] || 0;
  };

  const handleIncrement = (soldierType: SoldierType) => {
    if (!myPlayer) return;

    const cost = gameState.unitStats[soldierType].cost;
    const currentSpent = Object.entries(localArmyCounts).reduce((total, [type, count]) => {
      if (type === 'King') return total;
      const unitCost = gameState.unitStats[type as SoldierType].cost;
      return total + (count * unitCost);
    }, 0);

    const remainingPoints = myPlayer.raidPoints - currentSpent;

    if (remainingPoints < cost) {
      alert('Not enough raid points!');
      return;
    }

    setLocalArmyCounts(prev => ({
      ...prev,
      [soldierType]: prev[soldierType] + 1
    }));
    setHasPendingChanges(true);
  };

  const handleDecrement = (soldierType: SoldierType) => {
    if (!myPlayer) return;

    if (localArmyCounts[soldierType] > 0) {
      setLocalArmyCounts(prev => ({
        ...prev,
        [soldierType]: prev[soldierType] - 1
      }));
      setHasPendingChanges(true);
    }
  };

  const getTotalUnits = (): number => {
    return (
      localArmyCounts['Knight'] +
      localArmyCounts['Archer'] +
      localArmyCounts['Crossbow'] +
      localArmyCounts['Healer'] +
      localArmyCounts['Siege Equipment']
    );
  };

  const getArmyStats = (): { hp: number; damage: number } => {
    let hp = 0;
    let damage = 0;

    if (localArmyCounts['King'] > 0) {
      hp += gameState.unitStats['King'].hp;
      damage += gameState.unitStats['King'].damage;
    }

    hp += localArmyCounts['Knight'] * gameState.unitStats['Knight'].hp;
    damage += localArmyCounts['Knight'] * gameState.unitStats['Knight'].damage;

    hp += localArmyCounts['Archer'] * gameState.unitStats['Archer'].hp;
    damage += localArmyCounts['Archer'] * gameState.unitStats['Archer'].damage;

    hp += localArmyCounts['Crossbow'] * gameState.unitStats['Crossbow'].hp;
    damage += localArmyCounts['Crossbow'] * gameState.unitStats['Crossbow'].damage;

    hp += localArmyCounts['Healer'] * gameState.unitStats['Healer'].hp;
    damage += localArmyCounts['Healer'] * gameState.unitStats['Healer'].damage;

    hp += localArmyCounts['Siege Equipment'] * gameState.unitStats['Siege Equipment'].hp;
    damage += localArmyCounts['Siege Equipment'] * gameState.unitStats['Siege Equipment'].damage;

    return { hp, damage };
  };

  const handleResetArmy = async () => {
    if (!myPlayer) return;
    setShowResetModal(true);
  };

  const confirmReset = async () => {
    if (!myPlayer) return;

    setShowResetModal(false);
    setIsResetting(true);
    try {
      await resetPlayerArmy(myPlayer.id);
    } catch (err) {
      console.error('Failed to reset army:', err);
      alert('Failed to reset army. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!myPlayer || !hasPendingChanges) return;

    setIsSaving(true);
    try {
      // Calculate the new army structure based on local counts
      const updatedArmy: Army = {
        king: localArmyCounts['King'] > 0
          ? (myPlayer.army.king || { id: `king-${Date.now()}`, type: 'King' })
          : null,
        knights: Array.from({ length: localArmyCounts['Knight'] }, (_, i) =>
          myPlayer.army.knights[i] || { id: `knight-${Date.now()}-${i}`, type: 'Knight' }
        ),
        archers: Array.from({ length: localArmyCounts['Archer'] }, (_, i) =>
          myPlayer.army.archers[i] || { id: `archer-${Date.now()}-${i}`, type: 'Archer' }
        ),
        crossbows: Array.from({ length: localArmyCounts['Crossbow'] }, (_, i) =>
          myPlayer.army.crossbows[i] || { id: `crossbow-${Date.now()}-${i}`, type: 'Crossbow' }
        ),
        healers: Array.from({ length: localArmyCounts['Healer'] }, (_, i) =>
          myPlayer.army.healers[i] || { id: `healer-${Date.now()}-${i}`, type: 'Healer' }
        ),
        siegeEquipment: Array.from({ length: localArmyCounts['Siege Equipment'] }, (_, i) =>
          myPlayer.army.siegeEquipment[i] || {
            id: `siege-${Date.now()}-${i}`,
            type: 'Siege Equipment',
            manning: []
          }
        ) as SiegeEquipment[],
      };

      // Calculate new spent points
      const newSpent = Object.entries(localArmyCounts).reduce((total, [type, count]) => {
        if (type === 'King') return total;
        const unitCost = gameState.unitStats[type as SoldierType].cost;
        return total + (count * unitCost);
      }, 0);

      // Update player via gameService
      await gameService.updatePlayer(myPlayer.id, {
        army: updatedArmy,
        raidPointsSpent: newSpent,
      });

      // Refresh game state
      setHasPendingChanges(false);

      // Force a re-fetch of game state to sync
      window.location.reload();
    } catch (err) {
      console.error('Failed to save army changes:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (!myPlayer) return;

    // Reset local state to match server state
    setLocalArmyCounts({
      'King': myPlayer.army.king ? 1 : 0,
      'Knight': myPlayer.army.knights.length,
      'Archer': myPlayer.army.archers.length,
      'Crossbow': myPlayer.army.crossbows.length,
      'Healer': myPlayer.army.healers.length,
      'Siege Equipment': myPlayer.army.siegeEquipment.length,
    });
    setHasPendingChanges(false);
  };

  const getUnitEmoji = (type: SoldierType): string => {
    switch (type) {
      case 'King': return '👑';
      case 'Knight': return '⚔️';
      case 'Archer': return '🏹';
      case 'Crossbow': return '🎯';
      case 'Healer': return '✚';
      case 'Siege Equipment': return '🏛️';
      default: return '❓';
    }
  };

  if (isLoading) {
    return (
      <div className="my-army-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="my-army-container">
        <div className="not-authenticated">
          <h2>Please log in to manage your army</h2>
          <p>Use the login button in the navigation to authenticate with Twitch.</p>
        </div>
      </div>
    );
  }

  if (!myPlayer) {
    return (
      <div className="my-army-container">
        <div className="splash-screen">
          <div className="splash-content">
            <h1>🏰 You have not joined the game yet!</h1>
            <p>Please ask the broadcaster to add you to the game.</p>
            <p className="help-text">The administrator can add you via the Admin Area.</p>
          </div>
        </div>
      </div>
    );
  }

  const stats = getArmyStats();

  return (
    <div className="my-army-container">
      <div className={`my-army-header ${myPlayer.team}`}>
        <h1>My Army</h1>
        <div className="player-info-section">
          <div className="info-item">
            <span className="label">Player:</span>
            <span className="value">{myPlayer.name}</span>
          </div>
          <div className="info-item">
            <span className="label">Team:</span>
            <span className="value team-badge">{myPlayer.team === 'raiders' ? 'Raiders' : 'Defenders'}</span>
          </div>
          <div className="info-item">
            <span className="label">Raid Points:</span>
            <span className="value points">{myPlayer.raidPoints - myPlayer.raidPointsSpent} / {myPlayer.raidPoints}</span>
          </div>
        </div>
      </div>

      <div className="army-stats-panel">
        <div className="stat-card">
          <div className="stat-label">Total Units</div>
          <div className="stat-value">{getTotalUnits()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total HP</div>
          <div className="stat-value">{stats.hp}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Damage</div>
          <div className="stat-value">{stats.damage}</div>
        </div>
      </div>

      {/* Army Preview */}
      {getTotalUnits() > 0 && (
        <div className="army-preview-section">
          <h2>Army Preview</h2>
          <svg className="army-preview-svg" viewBox="0 0 600 150" preserveAspectRatio="xMidYMid meet">
            {/* Background */}
            <rect x="0" y="0" width="600" height="150" fill="#8b7355" />

            {/* Render units */}
            {(() => {
              const unitTypes: { type: SoldierType; count: number }[] = [];
              if (localArmyCounts['King'] > 0) unitTypes.push({ type: 'King', count: localArmyCounts['King'] });
              if (localArmyCounts['Knight'] > 0) unitTypes.push({ type: 'Knight', count: localArmyCounts['Knight'] });
              if (localArmyCounts['Healer'] > 0) unitTypes.push({ type: 'Healer', count: localArmyCounts['Healer'] });
              if (localArmyCounts['Archer'] > 0) unitTypes.push({ type: 'Archer', count: localArmyCounts['Archer'] });
              if (localArmyCounts['Crossbow'] > 0) unitTypes.push({ type: 'Crossbow', count: localArmyCounts['Crossbow'] });
              if (localArmyCounts['Siege Equipment'] > 0) unitTypes.push({ type: 'Siege Equipment', count: localArmyCounts['Siege Equipment'] });

              const totalGroups = unitTypes.length;
              const groupSpacing = 80;
              const startX = (600 - (totalGroups - 1) * groupSpacing) / 2;
              const y = 75;
              const size = 65;

              return unitTypes.map((unitType, index) => {
                const x = startX + index * groupSpacing;
                // For King, use team-specific image URL
                let imageUrl = gameState.unitStats[unitType.type]?.imageUrl;
                if (unitType.type === 'King') {
                  if (myPlayer.team === 'raiders' && gameState.kingRaidersImageUrl) {
                    imageUrl = gameState.kingRaidersImageUrl;
                  } else if (myPlayer.team === 'defenders' && gameState.kingDefendersImageUrl) {
                    imageUrl = gameState.kingDefendersImageUrl;
                  }
                }

                return (
                  <g key={`${unitType.type}-${index}`}>
                    {imageUrl ? (
                      <image
                        x={x - size / 2}
                        y={y - size / 2}
                        width={size}
                        height={size}
                        href={imageUrl}
                        preserveAspectRatio="xMidYMid meet"
                      />
                    ) : (
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="40"
                      >
                        {getUnitEmoji(unitType.type)}
                      </text>
                    )}

                    {/* Count badge */}
                    {unitType.count > 1 && (
                      <>
                        <circle
                          cx={x + size / 2 - 5}
                          cy={y - size / 2 + 5}
                          r={12}
                          fill="#dc3545"
                          stroke="#fff"
                          strokeWidth="2"
                        />
                        <text
                          x={x + size / 2 - 5}
                          y={y - size / 2 + 5}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="12"
                          fontWeight="bold"
                          fill="#fff"
                        >
                          {unitType.count}
                        </text>
                      </>
                    )}
                  </g>
                );
              });
            })()}
          </svg>
        </div>
      )}

      <div className="army-management-section">
        <div className="management-header">
          <h2>Manage Your Units</h2>
          <div className="management-actions">
            {hasPendingChanges && (
              <div className="pending-changes-banner">
                <span className="pending-changes-text">⚠️ Unsaved changes</span>
                <div className="pending-changes-actions">
                  <button
                    onClick={handleDiscardChanges}
                    disabled={isSaving}
                    className="discard-btn"
                  >
                    Discard
                  </button>
                  <button
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    className="save-btn"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={handleResetArmy}
              disabled={isResetting || getTotalUnits() === 0}
              className="reset-army-btn"
              title="Reset your army (keeps King and raid points)"
            >
              {isResetting ? '🔄 Resetting...' : '🔄 Reset Army'}
            </button>
          </div>
        </div>

        <div className="units-grid">
          <div className="unit-counter">
            <div className="unit-label">
              <span className="unit-icon">👑</span>
              <span>King</span>
              <span className="unit-info">(Free)</span>
            </div>
            <div className="counter-controls">
              <button
                onClick={() => handleDecrement('King')}
                disabled={getUnitCount('King') === 0}
              >
                -
              </button>
              <span className="count">{getUnitCount('King')}</span>
              <button
                onClick={() => handleIncrement('King')}
                disabled={getUnitCount('King') >= 1}
              >
                +
              </button>
            </div>
          </div>

          <div className="unit-counter">
            <div className="unit-label">
              <span className="unit-icon">⚔️</span>
              <span>Knights</span>
              <span className="unit-info">({gameState.unitStats['Knight'].cost} pts)</span>
            </div>
            <div className="counter-controls">
              <button
                onClick={() => handleDecrement('Knight')}
                disabled={getUnitCount('Knight') === 0}
              >
                -
              </button>
              <span className="count">{getUnitCount('Knight')}</span>
              <button onClick={() => handleIncrement('Knight')}>+</button>
            </div>
          </div>

          <div className="unit-counter">
            <div className="unit-label">
              <span className="unit-icon">🏹</span>
              <span>Archers</span>
              <span className="unit-info">({gameState.unitStats['Archer'].cost} pts)</span>
            </div>
            <div className="counter-controls">
              <button
                onClick={() => handleDecrement('Archer')}
                disabled={getUnitCount('Archer') === 0}
              >
                -
              </button>
              <span className="count">{getUnitCount('Archer')}</span>
              <button onClick={() => handleIncrement('Archer')}>+</button>
            </div>
          </div>

          <div className="unit-counter">
            <div className="unit-label">
              <span className="unit-icon">🎯</span>
              <span>Crossbows</span>
              <span className="unit-info">({gameState.unitStats['Crossbow'].cost} pts)</span>
            </div>
            <div className="counter-controls">
              <button
                onClick={() => handleDecrement('Crossbow')}
                disabled={getUnitCount('Crossbow') === 0}
              >
                -
              </button>
              <span className="count">{getUnitCount('Crossbow')}</span>
              <button onClick={() => handleIncrement('Crossbow')}>+</button>
            </div>
          </div>

          <div className="unit-counter">
            <div className="unit-label">
              <span className="unit-icon">✚</span>
              <span>Healers</span>
              <span className="unit-info">({gameState.unitStats['Healer'].cost} pts)</span>
            </div>
            <div className="counter-controls">
              <button
                onClick={() => handleDecrement('Healer')}
                disabled={getUnitCount('Healer') === 0}
              >
                -
              </button>
              <span className="count">{getUnitCount('Healer')}</span>
              <button onClick={() => handleIncrement('Healer')}>+</button>
            </div>
          </div>

          <div className="unit-counter">
            <div className="unit-label">
              <span className="unit-icon">🏛️</span>
              <span>Siege Equipment</span>
              <span className="unit-info">({gameState.unitStats['Siege Equipment'].cost} pts)</span>
            </div>
            <div className="counter-controls">
              <button
                onClick={() => handleDecrement('Siege Equipment')}
                disabled={getUnitCount('Siege Equipment') === 0}
              >
                -
              </button>
              <span className="count">{getUnitCount('Siege Equipment')}</span>
              <button onClick={() => handleIncrement('Siege Equipment')}>+</button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showResetModal}
        title="🔄 Reset Army"
        message={
          <div>
            <p>Are you sure you want to reset your army?</p>
            <ul>
              <li>Remove all your units (except the King)</li>
              <li>Reset your spent points to 0</li>
              <li>Keep your total raid points</li>
            </ul>
            <p>You can rebuild your army after reset.</p>
          </div>
        }
        confirmText="Reset Army"
        cancelText="Cancel"
        onConfirm={confirmReset}
        onCancel={() => setShowResetModal(false)}
        danger={true}
      />
    </div>
  );
};

export default MyArmy;
